/**
 * ShareSheet — the ONE share modal, used everywhere (#170).
 *
 * Replaces three older surfaces that each did a slice of "items → groups":
 * `ShareSubsetSheet` (scan), the scene per-card select circles +
 * `AddItemToGroupSheet`, and ItemDetail's "+ Add to group". Now a single
 * component handles all of it with the same mental model:
 *
 *   • Each of your groups is a row. Expand it → a horizontal strip of the
 *     candidate items. Tick which items go to THAT group — independently
 *     per group (Group A can get items 1,2; Group B items 3,4).
 *   • "Share with someone new" → email + name + type → stages a brand-new
 *     group (created lazily on commit, Q1) so 1:1 sharing is just "a group
 *     with one invited member" (no special path).
 *   • Scene / ItemDetail open it PRE-FILLED with current share-state, so
 *     ticking adds and un-ticking removes (unshare).
 *
 * The sheet is purely presentational: it emits a `SharePlan` via onApply.
 * The parent persists it — scene/ItemDetail via `commitSharePlanToServer`,
 * scan-save via confirm/batch `extras` (items aren't saved yet).
 *
 * Inventory membership (accept / verify / delete) is a SEPARATE concern
 * that lives on the scan + scene cards and is untouched by this sheet.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

import { fpColors } from '../../../styles/theme';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { loadGroups, selectGroups } from '../groupsSlice';
import { iconForKind } from '../groupKindMeta';
import { ItemThumbnail } from '../../../components/ItemThumbnail';
import { BboxCropThumbnail } from '../../../components/BboxCropThumbnail';
import type {
  GroupKind,
  GroupListItem,
  InventoryItemResponse,
} from '../../../api/types';

// ── plan emitted to the parent ──────────────────────────────────────
export interface PendingNewGroup {
  name: string;
  kind: GroupKind;
  email: string;
  itemIds: number[];
}
export interface SharePlan {
  /** existing groupId → itemIds the user wants shared with it */
  selections: Record<number, number[]>;
  /** brand-new "share with someone new" groups, created on commit */
  pendingNew: PendingNewGroup[];
}

export interface ShareSheetProps {
  visible: boolean;
  onClose: () => void;
  mode: 'scan' | 'scene' | 'item';
  /** Candidate items. scan = accepted scan items; scene = scene items;
   *  item = exactly one. */
  items: InventoryItemResponse[];
  /** existing groupId → itemIds ALREADY shared. Empty for scan. */
  initialShares?: Record<number, number[]>;
  /** #170 — the scan/scene photo. When an item has no cutout yet
   *  (`primary_photo_url`), the cell crops this to the item's bbox so
   *  the thumbnail is still real + distinct (no gray placeholder). */
  sourceImageUrl?: string | null;
  /** Parent persists the plan + closes. (scan stages; scene/item commits) */
  onApply: (plan: SharePlan) => void;
  applying?: boolean;
}

const KIND_OPTIONS: { label: string; kind: GroupKind }[] = [
  { label: 'Friends', kind: 'FRIENDS' },
  { label: 'Family', kind: 'FAMILY' },
  { label: 'Team', kind: 'TEAM' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const idOf = (it: InventoryItemResponse): number | null =>
  typeof (it as any)?.id === 'number' ? (it as any).id : null;

export const ShareSheet: React.FC<ShareSheetProps> = ({
  visible,
  onClose,
  mode,
  items,
  initialShares = {},
  sourceImageUrl,
  onApply,
  applying = false,
}) => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const groups = useAppSelector(selectGroups);

  const isSingle = mode === 'item';
  const allItemIds = useMemo(
    () => items.map(idOf).filter((x): x is number => x != null),
    [items],
  );

  // selected: key = groupId (>0) or negative tempId for pending-new groups.
  const [selected, setSelected] = useState<Map<number, Set<number>>>(new Map());
  const [pendingMeta, setPendingMeta] = useState<
    Map<number, { name: string; kind: GroupKind; email: string }>
  >(new Map());
  const [expandedKey, setExpandedKey] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const tempIdRef = useRef(-1);

  // add-person form
  const [email, setEmail] = useState('');
  const [groupName, setGroupName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [kind, setKind] = useState<GroupKind>('FRIENDS');

  // Seed state from initialShares whenever the sheet opens.
  useEffect(() => {
    if (!visible) return;
    dispatch(loadGroups());
    const seed = new Map<number, Set<number>>();
    for (const [gid, ids] of Object.entries(initialShares)) {
      seed.set(Number(gid), new Set(ids));
    }
    setSelected(seed);
    setPendingMeta(new Map());
    setExpandedKey(null);
    setAddOpen(false);
    setEmail('');
    setGroupName('');
    setNameEdited(false);
    setKind('FRIENDS');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  /** Eligible groups = non-PERSONAL, non-archived, viewer owns/admins. */
  const eligible = useMemo<GroupListItem[]>(
    () =>
      (groups ?? []).filter((g) => {
        if (g.archived_at) return false;
        if (g.kind === 'PERSONAL') return false;
        const role = (g as any).my_role;
        return role === 'OWNER' || role === 'ADMIN';
      }),
    [groups],
  );

  // ── selection mutators ───────────────────────────────────────────
  const setFor = (key: number, next: Set<number>) =>
    setSelected((prev) => {
      const m = new Map(prev);
      m.set(key, next);
      return m;
    });
  const toggleItem = (key: number, itemId: number) => {
    const cur = new Set(selected.get(key) ?? []);
    if (cur.has(itemId)) cur.delete(itemId);
    else cur.add(itemId);
    setFor(key, cur);
  };
  const selectAll = (key: number) => setFor(key, new Set(allItemIds));
  const clearGroup = (key: number) => setFor(key, new Set());
  const toggleSingle = (key: number) => {
    const id = allItemIds[0];
    if (id == null) return;
    toggleItem(key, id);
  };

  // ── cell state for the strip ──────────────────────────────────────
  type Cell = 'shared' | 'add' | 'remove' | 'none';
  const cellState = (key: number, itemId: number): Cell => {
    const sel = selected.get(key)?.has(itemId) ?? false;
    const wasShared = key > 0 && (initialShares[key] ?? []).includes(itemId);
    if (sel && wasShared) return 'shared';
    if (sel && !wasShared) return 'add';
    if (!sel && wasShared) return 'remove';
    return 'none';
  };

  // ── footer diff ───────────────────────────────────────────────────
  const { adds, removes, sharingGroupCount } = useMemo(() => {
    let a = 0;
    let r = 0;
    let g = 0;
    for (const [key, set] of selected) {
      const initial = key > 0 ? new Set(initialShares[key] ?? []) : new Set<number>();
      let groupAdds = 0;
      for (const id of set) if (!initial.has(id)) groupAdds += 1;
      let groupRemoves = 0;
      for (const id of initial) if (!set.has(id)) groupRemoves += 1;
      a += groupAdds;
      r += groupRemoves;
      if (set.size > 0) g += 1;
    }
    return { adds: a, removes: r, sharingGroupCount: g };
  }, [selected, initialShares]);

  const canCommit = mode === 'scan' ? true : adds + removes > 0;

  const buildPlan = (): SharePlan => {
    const selections: Record<number, number[]> = {};
    const pendingNew: PendingNewGroup[] = [];
    for (const [key, set] of selected) {
      const ids = Array.from(set);
      if (key > 0) {
        // include if non-empty OR it had initial shares (so removals diff)
        if (ids.length > 0 || (initialShares[key] ?? []).length > 0) {
          selections[key] = ids;
        }
      } else {
        const meta = pendingMeta.get(key);
        if (meta && ids.length > 0) {
          pendingNew.push({ ...meta, itemIds: ids });
        }
      }
    }
    return { selections, pendingNew };
  };

  // ── add-person ────────────────────────────────────────────────────
  const onEmailChange = (v: string) => {
    setEmail(v);
    if (!nameEdited) {
      const local = v.split('@')[0]?.trim();
      setGroupName(local ? `Shared with ${local}` : '');
    }
  };
  const emailValid = EMAIL_RE.test(email.trim());
  const canAddPerson = emailValid && groupName.trim().length > 0;

  const addPerson = () => {
    if (!canAddPerson) return;
    const tempId = tempIdRef.current;
    tempIdRef.current -= 1;
    setPendingMeta((prev) => {
      const m = new Map(prev);
      m.set(tempId, { name: groupName.trim(), kind, email: email.trim() });
      return m;
    });
    // pre-select all items for a brand-new group (the common case); user
    // can trim. Mirrors the "share all by default" feel.
    setFor(tempId, new Set(allItemIds));
    setExpandedKey(tempId);
    setAddOpen(false);
    setEmail('');
    setGroupName('');
    setNameEdited(false);
    setKind('FRIENDS');
  };

  const goCreateGroup = () => {
    onClose();
    setTimeout(() => navigation.navigate('CreateGroup'), 200);
  };

  // ── render helpers ────────────────────────────────────────────────
  const headerTitle =
    mode === 'item'
      ? `Share "${items[0]?.item_name || 'item'}"`
      : 'Share to circles';
  const headerSub =
    mode === 'item'
      ? undefined
      : `${allItemIds.length} item${allItemIds.length === 1 ? '' : 's'}${
          mode === 'scan' ? ' from this scan' : ''
        }`;
  const ctaLabel =
    mode === 'scan'
      ? sharingGroupCount > 0
        ? `Done · ${sharingGroupCount} circle${sharingGroupCount === 1 ? '' : 's'}`
        : 'Done'
      : `Apply · +${adds} add  −${removes} remove`;

  const renderStrip = (key: number) => (
    <>
      {!isSingle ? (
        <View style={styles.bulkRow}>
          <Pressable onPress={() => selectAll(key)} hitSlop={6}>
            <Text style={styles.bulkBtn}>Select all</Text>
          </Pressable>
          <Text style={styles.bulkDot}>·</Text>
          <Pressable onPress={() => clearGroup(key)} hitSlop={6}>
            <Text style={styles.bulkBtn}>Clear</Text>
          </Pressable>
        </View>
      ) : null}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.strip}
      >
        {items.map((it) => {
          const id = idOf(it);
          if (id == null) return null;
          const st = cellState(key, id);
          return (
            <Pressable
              key={id}
              onPress={() => toggleItem(key, id)}
              style={styles.cell}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: st === 'shared' || st === 'add' }}
              accessibilityLabel={`${it.item_name || 'item'} for this circle`}
              testID={`share-cell-${key}-${id}`}
            >
              <View style={styles.cellThumbWrap}>
                {!((it as any).primary_photo_url) &&
                sourceImageUrl &&
                (it as any).bbox ? (
                  <BboxCropThumbnail
                    sourceUrl={sourceImageUrl}
                    bbox={(it as any).bbox}
                    size={56}
                    shape="square"
                  />
                ) : (
                  <ItemThumbnail item={it} size={56} shape="square" />
                )}
                <View
                  style={[
                    styles.cellBadge,
                    st === 'shared' && styles.badgeShared,
                    st === 'add' && styles.badgeAdd,
                    st === 'remove' && styles.badgeRemove,
                    st === 'none' && styles.badgeNone,
                  ]}
                >
                  {st === 'shared' ? (
                    <MaterialIcons name="check-circle" size={16} color="#fff" />
                  ) : st === 'add' ? (
                    <MaterialIcons name="check" size={14} color="#fff" />
                  ) : st === 'remove' ? (
                    <MaterialIcons name="close" size={14} color="#fff" />
                  ) : null}
                </View>
              </View>
              <Text style={styles.cellName} numberOfLines={1}>
                {it.item_name || 'Item'}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </>
  );

  const renderGroupRow = (
    key: number,
    name: string,
    kindStr: string,
    sub: string,
    pending: boolean,
  ) => {
    const set = selected.get(key) ?? new Set<number>();
    const k = set.size;
    const expanded = expandedKey === key;
    const singleOn = isSingle && allItemIds[0] != null && set.has(allItemIds[0]);
    return (
      <View key={key} style={[styles.groupRow, expanded && styles.groupRowOpen]}>
        <Pressable
          onPress={() =>
            isSingle ? toggleSingle(key) : setExpandedKey(expanded ? null : key)
          }
          style={styles.groupHead}
          accessibilityRole="button"
          accessibilityLabel={name}
          testID={`share-group-${key}`}
        >
          <View style={styles.groupIcon}>
            <MaterialIcons
              name={pending ? 'person-add' : iconForKind(kindStr)}
              size={18}
              color={fpColors.primary}
            />
          </View>
          <View style={styles.groupMeta}>
            <Text style={styles.groupName} numberOfLines={1}>
              {name}
            </Text>
            <Text style={styles.groupSub} numberOfLines={1}>
              {pending ? `will invite on save · ${sub}` : sub}
            </Text>
          </View>
          {isSingle ? (
            <View style={[styles.toggle, singleOn && styles.toggleOn]}>
              <Text style={[styles.toggleText, singleOn && styles.toggleTextOn]}>
                {singleOn ? 'ON' : 'OFF'}
              </Text>
            </View>
          ) : (
            <>
              <Text style={styles.countBadge}>
                {k} of {allItemIds.length}
              </Text>
              <MaterialIcons
                name={expanded ? 'expand-less' : 'expand-more'}
                size={22}
                color={fpColors.outline}
              />
            </>
          )}
        </Pressable>
        {!isSingle && expanded ? renderStrip(key) : null}
      </View>
    );
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel="Close" />
        <View style={styles.sheet} testID="share-sheet">
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>{headerTitle}</Text>
              {headerSub ? <Text style={styles.sub}>{headerSub}</Text> : null}
            </View>
            <Pressable onPress={onClose} hitSlop={10} accessibilityLabel="Close">
              <MaterialIcons name="close" size={22} color={fpColors.onSurfaceVariant} />
            </Pressable>
          </View>

          <Text style={styles.caption}>
            {mode === 'scene'
              ? 'Tap to share · tap a ◉ shared item to remove it.'
              : mode === 'item'
                ? 'Turn a circle on to share this item with it.'
                : 'Everything saves to your Personal inventory. Open a circle to pick which items to share.'}
          </Text>

          <ScrollView style={styles.body} keyboardShouldPersistTaps="handled">
            {eligible.length === 0 && pendingMeta.size === 0 ? (
              <View style={styles.empty}>
                <MaterialIcons name="groups" size={34} color={fpColors.outline} />
                <Text style={styles.emptyTitle}>No circles yet</Text>
                <Text style={styles.emptyBody}>
                  Create a circle, or add someone by email below, to share these
                  items.
                </Text>
              </View>
            ) : null}

            {/* #170 #1 — "Share with someone new" pinned at the TOP, its
                own accent section, segregated from your groups. */}
            <View style={[styles.addPersonCard, addOpen && styles.addPersonCardOpen]}>
              <Pressable
                onPress={() => setAddOpen((v) => !v)}
                style={styles.groupHead}
                accessibilityRole="button"
                accessibilityLabel="Share with someone new"
                testID="share-add-person-toggle"
              >
                <View style={styles.addPersonIcon}>
                  <MaterialIcons name="mail-outline" size={18} color={fpColors.onPrimary} />
                </View>
                <View style={styles.groupMeta}>
                  <Text style={styles.groupName}>Share with someone new</Text>
                  <Text style={styles.groupSub}>invite by email</Text>
                </View>
                <MaterialIcons
                  name={addOpen ? 'expand-less' : 'expand-more'}
                  size={22}
                  color={fpColors.primary}
                />
              </Pressable>
              {addOpen ? (
                <View style={styles.addForm}>
                  <Text style={styles.fieldLabel}>Email</Text>
                  <TextInput
                    value={email}
                    onChangeText={onEmailChange}
                    placeholder="name@example.com"
                    placeholderTextColor={fpColors.outline}
                    autoCapitalize="none"
                    keyboardType="email-address"
                    style={styles.input}
                    testID="share-add-email"
                  />
                  <Text style={styles.fieldLabel}>Circle name</Text>
                  <TextInput
                    value={groupName}
                    onChangeText={(v) => {
                      setGroupName(v);
                      setNameEdited(true);
                    }}
                    placeholder="Shared with …"
                    placeholderTextColor={fpColors.outline}
                    style={styles.input}
                    testID="share-add-name"
                  />
                  <Text style={styles.fieldLabel}>Circle type</Text>
                  <View style={styles.kindRow}>
                    {KIND_OPTIONS.map((opt) => {
                      const on = kind === opt.kind;
                      return (
                        <Pressable
                          key={opt.kind}
                          onPress={() => setKind(opt.kind)}
                          style={[styles.kindChip, on && styles.kindChipOn]}
                          accessibilityRole="radio"
                          accessibilityState={{ selected: on }}
                          testID={`share-add-kind-${opt.kind}`}
                        >
                          <Text style={[styles.kindText, on && styles.kindTextOn]}>
                            {opt.label}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                  <Text style={styles.addNote}>
                    They get an email invite and see these items once they
                    accept.
                  </Text>
                  <Pressable
                    onPress={addPerson}
                    disabled={!canAddPerson}
                    style={[styles.addBtn, !canAddPerson && { opacity: 0.5 }]}
                    accessibilityRole="button"
                    accessibilityLabel="Add and pick items"
                    testID="share-add-confirm"
                  >
                    <Text style={styles.addBtnText}>Add &amp; pick items</Text>
                  </Pressable>
                </View>
              ) : null}
            </View>

            {eligible.length > 0 || pendingMeta.size > 0 ? (
              <Text style={styles.sectionHeader}>YOUR CIRCLES</Text>
            ) : null}

            {/* staged new-person groups (most recently added on top) */}
            {Array.from(pendingMeta.entries()).map(([tempId, meta]) =>
              renderGroupRow(tempId, meta.name, meta.kind, meta.email, true),
            )}

            {/* existing groups */}
            {eligible.map((g) =>
              renderGroupRow(
                g.id,
                g.name,
                g.kind,
                `${(g as any).kind?.toLowerCase?.() || 'circle'} · ${
                  g.member_count ?? 0
                } member${(g.member_count ?? 0) === 1 ? '' : 's'}`,
                false,
              ),
            )}
          </ScrollView>

          {/* footer */}
          <Pressable
            onPress={() => {
              if (!canCommit || applying) return;
              onApply(buildPlan());
            }}
            disabled={!canCommit || applying}
            style={[styles.cta, (!canCommit || applying) && { opacity: 0.5 }]}
            accessibilityRole="button"
            accessibilityLabel={ctaLabel}
            testID="share-sheet-cta"
          >
            <Text style={styles.ctaText}>{applying ? 'Applying…' : ctaLabel}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  backdrop: { flex: 1 },
  sheet: {
    backgroundColor: fpColors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 20,
    maxHeight: '88%',
  },
  header: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  title: { fontFamily: 'Inter_800ExtraBold', fontSize: 19, color: fpColors.onSurface },
  sub: { fontFamily: 'Inter_400Regular', fontSize: 12, color: fpColors.onSurfaceVariant, marginTop: 2 },
  caption: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    lineHeight: 16,
    marginTop: 8,
    marginBottom: 6,
  },
  // The sheet is content-sized up to maxHeight:88%. A rigid body (the RN
  // default flexShrink:0) overflowed and clipped the group list with no
  // scroll once there were more than ~2 groups. flexShrink:1 lets the body
  // yield space when the content exceeds the sheet height, so the list
  // scrolls internally between the fixed header/caption and the pinned
  // footer CTA (Material 3 bottom-sheet pattern).
  body: { flexShrink: 1, marginBottom: 4 },
  groupRow: {
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 12,
    marginBottom: 8,
    overflow: 'hidden',
  },
  groupRowOpen: { borderColor: fpColors.primary, backgroundColor: fpColors.surfaceContainerLow },
  // #170 #1 — pinned "share with someone new" accent card at the top.
  addPersonCard: {
    borderWidth: 1,
    borderColor: fpColors.primary,
    borderRadius: 12,
    marginBottom: 8,
    overflow: 'hidden',
    backgroundColor: fpColors.primaryContainer,
  },
  addPersonCardOpen: { backgroundColor: fpColors.surfaceContainerLow },
  addPersonIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: fpColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionHeader: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    letterSpacing: 0.6,
    color: fpColors.onSurfaceVariant,
    marginTop: 4,
    marginBottom: 8,
    marginLeft: 2,
  },
  groupHead: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12 },
  groupIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: fpColors.primaryContainer,
    alignItems: 'center',
    justifyContent: 'center',
  },
  groupMeta: { flex: 1 },
  groupName: { fontFamily: 'Inter_700Bold', fontSize: 14, color: fpColors.onSurface },
  groupSub: { fontFamily: 'Inter_400Regular', fontSize: 11, color: fpColors.onSurfaceVariant, marginTop: 1 },
  countBadge: { fontFamily: 'Inter_600SemiBold', fontSize: 12, color: fpColors.primary },
  toggle: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  toggleOn: { backgroundColor: fpColors.primary, borderColor: fpColors.primary },
  toggleText: { fontFamily: 'Inter_700Bold', fontSize: 11, color: fpColors.onSurfaceVariant },
  toggleTextOn: { color: fpColors.onPrimary },
  bulkRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingBottom: 4 },
  bulkBtn: { fontFamily: 'Inter_600SemiBold', fontSize: 12, color: fpColors.primary },
  bulkDot: { color: fpColors.outline },
  strip: { gap: 10, paddingHorizontal: 12, paddingBottom: 12 },
  cell: { width: 64, alignItems: 'center' },
  cellThumbWrap: { width: 56, height: 56, borderRadius: 8, overflow: 'visible' },
  cellBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: fpColors.surface,
  },
  badgeShared: { backgroundColor: fpColors.primary },
  badgeAdd: { backgroundColor: '#1f9d55' },
  badgeRemove: { backgroundColor: fpColors.error },
  badgeNone: { backgroundColor: 'rgba(0,0,0,0.18)' },
  cellName: { marginTop: 4, fontFamily: 'Inter_400Regular', fontSize: 10, color: fpColors.onSurfaceVariant, width: 60, textAlign: 'center' },
  addForm: { paddingHorizontal: 12, paddingBottom: 12, gap: 6 },
  fieldLabel: { fontFamily: 'Inter_600SemiBold', fontSize: 11, color: fpColors.onSurfaceVariant, marginTop: 4 },
  input: {
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    backgroundColor: fpColors.surface,
  },
  kindRow: { flexDirection: 'row', gap: 8 },
  kindChip: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  kindChipOn: { backgroundColor: fpColors.primaryContainer, borderColor: fpColors.primary },
  kindText: { fontFamily: 'Inter_600SemiBold', fontSize: 12, color: fpColors.onSurfaceVariant },
  kindTextOn: { color: fpColors.onPrimaryContainer },
  addNote: { fontFamily: 'Inter_400Regular', fontSize: 11, color: fpColors.onSurfaceVariant, lineHeight: 15, marginTop: 2 },
  addBtn: {
    marginTop: 6,
    paddingVertical: 11,
    borderRadius: 10,
    backgroundColor: fpColors.primaryContainer,
    alignItems: 'center',
  },
  addBtnText: { fontFamily: 'Inter_700Bold', fontSize: 13, color: fpColors.primary },
  empty: { alignItems: 'center', gap: 6, paddingVertical: 28 },
  emptyTitle: { fontFamily: 'Inter_700Bold', fontSize: 15, color: fpColors.onSurface },
  emptyBody: { fontFamily: 'Inter_400Regular', fontSize: 12, color: fpColors.onSurfaceVariant, textAlign: 'center', lineHeight: 17, paddingHorizontal: 24 },
  cta: {
    marginTop: 10,
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: fpColors.primary,
    alignItems: 'center',
  },
  ctaText: { fontFamily: 'Inter_700Bold', fontSize: 15, color: fpColors.onPrimary },
});

export default ShareSheet;
